import { Box, IconButton, Stack } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Logout, changePassword } from '../../../services/authService';
import { Toaster } from '../../../shared/components/common/toaster';
import { buildLocalizeRoute } from '../../../helpers/languageHelper';
import { AppRoutes } from '../../../constants/appRoutes';
import { BorrowerChangePasswordSchema } from '../../../shared/validations/schema';
import { FormHelper } from '../../../helpers/formHelper';
import { TextWrapper } from '../../../shared/components/formUtilities/useTextWrapper';
import { InputWrapper } from '../../../shared/components/formUtilities/useInput';
import { ButtonWrapper } from '../../../shared/components/formUtilities/useButton';
import { useTranslation } from 'react-i18next';
import { RegistrationNavBar } from '../../../pages/registration/components/registrationNavBar';
import { PasswordValidationPopover } from '../../../shared/components/common/passwordValidationPopover';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckIcon from '@mui/icons-material/Check';

export const PasswordExpired = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const [showOldPassword, setShowOldPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [showErrorPopover, setShowErrorPopover] = useState(false);

	const form = useFormik({
		initialValues: {
			newPassword: '',
			oldPassword: '',
			confirmPassword: '',
		},
		onSubmit: (values) => {
			setLoading(true);
			changePassword(values)
				.then((resp) => {
					setLoading(false);
					Toaster({ msg: t('passwordChangedSuccessfully'), isError: false });
					Logout();
					navigate(buildLocalizeRoute(AppRoutes.Login));
				})
				.catch((err) => {
					setLoading(false);
				});
		},
		validationSchema: BorrowerChangePasswordSchema,
		...FormHelper.formikProps(),
	});

	return (
		<>
			<RegistrationNavBar isLogin={true}></RegistrationNavBar>
			<div className="flex flex-col flex-auto items-center justify-center p-4 min-w-0 md:h-[calc(100%-130px)] auth-card-wrapper bg-white">
				<div className="w-full relative flex sm:w-auto rounded-2xl shadow-md bg-card bg-white">
					<div className="w-full md:p-12 p-8 pt-12 max-w-[400px] sm:w-[400px] mx-auto sm:mx-0">
						<div className="w-full flex flex-col flex-0 relative">
							<TextWrapper
								classList="!text-center"
								type="h6"
								text={t('passwordExpiredTitle')}
							/>
							<TextWrapper
								classList="!text-[15px] !caption !text-center !mt-2 !mb-2"
								type="h6"
								text={t('passwordExpiredMessage')}
							/>
						</div>
						<FormikProvider value={form}>
							<Stack
								className="text-center mt-6"
								gap={2}>
								<InputWrapper
									endIcon={
										<IconButton
											onClick={() => setShowOldPassword(!showOldPassword)}
											className="!bg-transparent text-primary">
											{!showOldPassword ? (
												<VisibilityOffOutlinedIcon />
											) : (
												<VisibilityOutlinedIcon />
											)}
										</IconButton>
									}
									type={showOldPassword ? 'text' : 'password'}
									label={t('existingPassword')}
									name="oldPassword"
									key={'oldPassword'}></InputWrapper>
								<Box className="relative">
									<PasswordValidationPopover
										controllerValue={form.values.newPassword}
										showErrorPopover={showErrorPopover}
									/>
									<InputWrapper
										onFocus={() => setShowErrorPopover(true)}
										onBlur={() => setShowErrorPopover(false)}
										endIcon={
											<IconButton
												onClick={() => setShowNewPassword(!showNewPassword)}
												className="!bg-transparent text-primary">
												{!showNewPassword ? (
													<VisibilityOffOutlinedIcon />
												) : (
													<VisibilityOutlinedIcon />
												)}
											</IconButton>
										}
										type={showNewPassword ? 'text' : 'password'}
										label={t('enterNewPassword')}
										name="newPassword"
										key={'newPassword'}></InputWrapper>
								</Box>
								<InputWrapper
									endIcon={
										<IconButton
											onClick={() => setShowConfirmPassword(!showConfirmPassword)}
											className="!bg-transparent text-primary">
											{!showConfirmPassword ? (
												<VisibilityOffOutlinedIcon />
											) : (
												<VisibilityOutlinedIcon />
											)}
										</IconButton>
									}
									type={showConfirmPassword ? 'text' : 'password'}
									label={t('confirmNewPassword')}
									name="confirmPassword"
									key={'confirmPassword'}></InputWrapper>
								<ButtonWrapper
									loading={loading}
									onClick={() => {
										FormHelper.validateForm(form);
									}}
									disabled={!form.isValid}
									icon={<CheckIcon></CheckIcon>}
									classList="!w-full"
									label={t('confirmPassword')}></ButtonWrapper>
							</Stack>
						</FormikProvider>
					</div>
				</div>
			</div>
		</>
	);
};
